import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api";
import {formatDateTime} from "../dates";
import { useCapability, useStaffSession } from "../ui/StaffSession";
import { withBuildingScope } from "../ui/building-scope";
import {
  readIntakeCreateReceipt,
  uploadIntakeOriginal,
} from "../ui/intake-upload-receipts";
import { ErrorState } from "../ui/State";
type RetainedUpload={id:string;name:string;created_at?:string;file_size:number;blocked_reason:string|null;building_name:string|null};
export function MailFileUpload({
  initial = [], children,
}: {
  children?:ReactNode;
  initial?: Array<{ id: string; name: string }>;
}) {
  const fieldset = useRef<HTMLFieldSetElement>(null);
  const canUpload = useCapability("documents:manage"),
    session = useStaffSession(),
    building =
      session?.effectiveScope.kind === "building"
        ? session.effectiveScope.id
        : undefined;
  const [files, setFiles] = useState(initial),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  const [query,setQuery]=useState(''),[mine,setMine]=useState(false),[page,setPage]=useState(1);
  const [retained,setRetained]=useState<{uploads:RetainedUpload[];hasMoreUploads:boolean}>();
  useEffect(()=>{
    const controller=new AbortController();
    const timer=setTimeout(()=>{
      const params=new URLSearchParams({q:query,page:String(page)});
      if(mine)params.set('mine','true');
      void api<{uploads:RetainedUpload[];hasMoreUploads:boolean}>(withBuildingScope('/api/communications/compose-options?'+params,building),{signal:controller.signal})
        .then(value=>{if(controller.signal.aborted)return;setRetained({...value,uploads:value.uploads??[]});setError(undefined)})
        .catch(e=>{if(!controller.signal.aborted)setError(e)});
    },query?250:0);
    return()=>{clearTimeout(timer);controller.abort()};
  },[query,mine,page,building]);
  useEffect(() => {
    const form = fieldset.current?.closest("form");
    if (!busy || !form) return;
    const block = (event: SubmitEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      setError(
        Error(
          "Wait for the attachment upload and security check before saving or sending.",
        ),
      );
    };
    form.addEventListener("submit", block, true);
    return () => form.removeEventListener("submit", block, true);
  }, [busy]);
  async function upload(selected: FileList | null) {
    if (!selected?.length || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      if (files.length + selected.length > 15)
        throw Error("Choose at most 15 uploaded attachments.");
      for (const file of Array.from(selected)) {
        if (!file.size || file.size > 10_000_000)
          throw Error("Choose a non-empty file no larger than 10 MB.");
        const checksumSha256 = Array.from(
          new Uint8Array(
            await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
          ),
        )
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        const manifest = { originalFileName: file.name, checksumSha256 };
        const request: typeof api = (url, options) =>
          api(withBuildingScope(url, building), options);
        const created = readIntakeCreateReceipt(
          await request("/api/document-ingestion/batches", {
            method: "POST",
            body: JSON.stringify({
              importKey: crypto.randomUUID(),
              sourceName: "Email attachment upload",
              defaultDevelopmentId: building,
              duplicatePolicy: "reuse-single",
              files: [
                {
                  ...manifest,
                  mediaType: file.type || "application/octet-stream",
                  fileSize: file.size,
                },
              ],
            }),
          }),
          [manifest],
          building,
          "reuse-single",
        );
        const item = created.items[0]!;
        const receipt = await uploadIntakeOriginal(
          request,
          item,
          created.id!,
          file,
          building,
        );
        if (
          ["Scan unavailable", "Quarantined", "Scan failed"].includes(
            String(receipt.contentStatus),
          )
        )
          throw Error(
            "The file is retained in Documents intake, but its security check is not clean. Resolve the scan before attaching it.",
          );
        setFiles((old) =>
          old.some((f) => f.id === item.id)
            ? old
            : [...old, { id: item.id, name: file.name }],
        );
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <fieldset ref={fieldset}>
      <legend>Add files — attach a copy</legend>
      {children}
      <details>
        <summary>Find uploaded files</summary>
        <label>Original filename<input value={query} placeholder="For example, Beulah Lodge meter housing FRA"
          onChange={event=>{setQuery(event.target.value);setPage(1);setRetained(undefined)}}/></label>
        <label><input type="checkbox" checked={mine} onChange={event=>{setMine(event.target.checked);setPage(1);setRetained(undefined)}}/>My uploads</label>
        <p>Recent uploads appear first, including files already filed under a different title. Choose an original to include in your email.</p>
        {!retained&&<p role="status">Finding files…</p>}
        {retained?.uploads.map(file=><label key={file.id}>
          <input type="checkbox" disabled={busy||Boolean(file.blocked_reason)} checked={files.some(f=>f.id===file.id)}
            onChange={event=>{if(event.target.checked&&files.length>=15){setError(Error('Choose at most 15 uploaded attachments.'));return}
              setFiles(old=>event.target.checked?[...old,{id:file.id,name:file.name}]:old.filter(f=>f.id!==file.id))}}/>
          <span>{file.name}<small>{file.building_name??'Portfolio'} · {Math.ceil(file.file_size/1024)} KB{file.created_at?' · Uploaded '+formatDateTime(file.created_at):''}{file.blocked_reason?' · '+file.blocked_reason:''}</small></span>
        </label>)}
        {retained&&!retained.uploads.length&&<p>No matching uploaded files in your current access context.</p>}
        <button type="button" disabled={page===1} onClick={()=>{setPage(p=>p-1);setRetained(undefined)}}>Previous files</button>
        <span> Page {page} </span>
        <button type="button" disabled={!retained?.hasMoreUploads} onClick={()=>{setPage(p=>p+1);setRetained(undefined)}}>More files</button>
      </details>
      {canUpload && (
        <label>
          Choose files
          <input
            type="file"
            multiple
            disabled={busy}
            onChange={(event) => {
              void upload(event.currentTarget.files);
              event.currentTarget.value = "";
            }}
          />
        </label>
      )}
      <p>
        {busy
          ? "Uploading and checking files…"
          : "Files stay in Documents intake. Each file must pass its security scan before sending. Total email attachment limit: 10 MB."}
      </p>
      {files.map((file) => (
        <div key={file.id}>
          <input type="hidden" name="intakeIds" value={file.id} />
          <span>{file.name}</span>{" "}
          <button
            type="button"
            className="quiet"
            disabled={busy}
            onClick={() =>
              setFiles((old) => old.filter((f) => f.id !== file.id))
            }
          >
            Remove attachment
          </button>
        </div>
      ))}
      {Boolean(error) && <ErrorState error={error} />}
    </fieldset>
  );
}
